import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { useLang, type Key } from '../i18n'
import { useAgentBusy } from '../lib/agent-busy'
import { actorDisplayName } from '../lib/actor-name'
import { buildHistoryRows, type HistoryRow } from '../lib/history-versions'
import { FlattenHistoryDialog } from './FlattenHistoryDialog'
import {
  isGridEntry,
  SUGGESTED_VERSION_TAGS,
  type FlattenCounts,
  VERSION_COMMENT_MAX,
  VERSION_TAG_MAX,
  type HistoryEntry,
  type SuggestedVersionTag,
  type VersionDiff,
  type VersionMeta,
  type VersionTag,
} from '../lib/types'
import { labelParts } from '../slides/labels'
import { useTourState } from '../tours/useTourState'
import { HISTORY_STATE, type HistoryTourState } from '../tours/probes'
// Only for the four rules a GRID entry's row needs (.hstruct/.hcells/.hcell-at/.htrunc). They live
// beside the grid editor rather than in styles.css — see the header of that file.
import './grid/grid.css'

/** Published while the window is open (a constant: publishing never re-runs the probes needlessly). */
const HISTORY_OPEN: HistoryTourState = { open: true }

/** A row's change detail shows at most this many lines; a longer one is clipped behind "Show more". */
const CHANGE_LINES = 3
/** Characters per line, to estimate a detail's height where the browser lays nothing out (tests). */
const EST_LINE_CHARS = 80

/** How many lines `text` takes at EST_LINE_CHARS per line (each paragraph at least one). */
function estimateLines(text: string): number {
  return text.split('\n').reduce((n, line) => n + Math.max(1, Math.ceil(line.length / EST_LINE_CHARS)), 0)
}

/** A tag or comment change, as PATCH /v1/versions/:version takes it (null = clear). */
export type VersionPatch = { tag?: VersionTag | null; comment?: string | null }

/**
 * The room's modification history (who changed what, when) in an accessible modal
 * dialog, laid out as a scannable table. Each row renders ONLY its changed window as
 * an inline diff — deletions struck through in red, additions in green — never the
 * whole document. Each row offers two actions, each behind a confirm step:
 * - **Restore** brings the part back, in place, to its state right after that entry;
 * - **Restore as part** leaves the part untouched and makes a new read-only copy of that state.
 * Neither is offered on the newest row: the part already IS that state, so restoring it would
 * change nothing and a copy would only duplicate the part.
 * An entry the parent cannot restore (`restorable` false: no restore point for it) shows both
 * actions disabled, with the reason in words — never a button that would fail. The live `entries`
 * are observed by the parent (Canvas), which also decides which actions apply (none for a viewer).
 * One modal for every part kind: prose rows are text windows, grid rows are cells, slides rows are
 * one line per slide touched, named through `slideName`.
 *
 * With `versions` (AB#241) the list goes on past the 50-entry log: the restore points older than it,
 * paged ("Show older"), each described by what it froze of its entry and its diff against the
 * previous surviving version (`loadDiff`). Log entries whose restore point was removed fold into
 * the next row ("3 changes merged"). Every row with a restore point carries its tag (free text,
 * Bronze / Silver / Gold suggested, as a chip) and comment — editable through `onUpdateVersion`,
 * read only without it (a viewer). Tag and comment behave alike: an empty one is a plain "Add …"
 * action, a set one has a pen that switches that one field into edit mode (Save / Cancel; Enter saves
 * a tag, Escape cancels; focus goes back to the pen), and the tag chip's × removes it. The tag and the
 * comment share one cell with the row's actions, which float right: the comment's first lines run
 * beside the buttons and the lines below them use the full width — it never overlaps them.
 * "Tagged only" and a search over tags and comments (case and accents aside) filter the list.
 *
 * A row's change detail (diff, cells, slide lines) shows at most CHANGE_LINES lines; a longer one
 * is clipped, fading out, with a "Show more" / "Show less" toggle. The comment is never clipped.
 *
 * With `onFlatten` (a bid admin or a tenant admin — AB#243) the table becomes a single-select grid:
 * a row with a restore point is selected by a click on it (or Space / Enter when it has focus) and
 * unselected the same way — the version that becomes the new start of the history (none selected =
 * the newest). Clicks on the row's own controls never toggle it. A "Flatten history…" action in the
 * header, beside the search, opens a confirm dialog naming that version, with the dry-run count,
 * before deleting every version older than it. A selection the search or "Tagged only" hides is
 * dropped. Disabled, with the reason, while an agent is editing; focus comes back to it after.
 */
export function HistoryModal({
  entries,
  onClose,
  onRestore,
  onRestoreAsPart,
  restorable,
  slideName,
  versions,
  hasOlder = false,
  loadingOlder = false,
  onShowOlder,
  loadDiff,
  onUpdateVersion,
  onRestoreVersion,
  onRestoreVersionAsPart,
  onFlattenPreview,
  onFlatten,
  locked = false,
}: {
  entries: HistoryEntry[]
  onClose: () => void
  /** Restore the part, in place, to its state right after this entry (omit to hide the action). */
  onRestore?: (entry: HistoryEntry) => void
  /** Create a read-only copy of the part as it was right after this entry (omit to hide it). */
  onRestoreAsPart?: (entry: HistoryEntry) => void
  /** Whether this entry can be restored at all (it has a restore point, or prose can rebuild it).
   *  Omitted: every entry can. */
  restorable?: (entry: HistoryEntry) => boolean
  /** A slides entry's slide, named by its place in the deck today ("Slide 2", or "A deleted slide").
   *  Omitted: the stored label is shown alone. */
  slideName?: (slide: string) => string
  /** The room's restore points read so far, newest first (GET /v1/versions pages). Omitted: the
   *  log alone, with no tags. */
  versions?: VersionMeta[] | null
  /** Another page of restore points lies below the oldest read. */
  hasOlder?: boolean
  loadingOlder?: boolean
  onShowOlder?: () => void
  /** What an older version changed since the previous surviving one (GET /v1/versions/:v/diff). */
  loadDiff?: (version: number) => Promise<VersionDiff>
  /** Tag / comment a version (omit for read only). Rejects when the server refused. */
  onUpdateVersion?: (version: number, patch: VersionPatch) => Promise<unknown>
  /** Restore in place / as a part to an OLDER version — one with no entry in the log. */
  onRestoreVersion?: (version: VersionMeta) => void
  onRestoreVersionAsPart?: (version: VersionMeta) => void
  /** Flatten's dry run: what deleting every version older than `keep` would remove. */
  onFlattenPreview?: (keep: number) => Promise<FlattenCounts>
  /** Flatten down to `keep` (omit, with the preview, to hide the action — anyone but an admin).
   *  Rejects when the server refused; the parent reloads the list on success. */
  onFlatten?: (keep: number) => Promise<unknown>
  /** A revision lock is held in the room (an agent at work): the server would refuse a flatten. */
  locked?: boolean
}) {
  const { t } = useLang()
  // Guided tours: the `workspace.historyOpen` probe reads this while the window is mounted.
  useTourState(HISTORY_STATE, HISTORY_OPEN)
  const busy = useAgentBusy().busy // block restore while an agent is editing (avoid conflicts)
  // The localised "{name}'s agent" form, shared with the lock bar through lib/actor-name.ts: an
  // edit the member's console agent wrote is attributed here exactly as the bar attributed the
  // lock it wrote it under, instead of showing a raw userId.
  const agentPattern = t('sec_lock_agent')
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  // The row and the action awaiting confirmation (one at a time).
  const [confirm, setConfirm] = useState<{ id: string; action: 'restore' | 'copy' } | null>(null)
  const [taggedOnly, setTaggedOnly] = useState(false)
  const [query, setQuery] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  // The one field being edited (one at a time in the whole modal).
  const [editing, setEditing] = useState<{ key: string; field: VersionField } | null>(null)
  const [flattening, setFlattening] = useState(false)
  // The row chosen as the new start of the history (flatten); null = the newest version.
  const [keepKey, setKeepKey] = useState<string | null>(null)
  const flattenRef = useRef<HTMLButtonElement>(null)
  const selectHintId = useId()
  const hasActions = Boolean(onRestore || onRestoreAsPart || onRestoreVersion || onRestoreVersionAsPart)
  const withVersions = versions !== undefined

  /** A slides label (the server's English words) in the member's language. */
  const changeLabel = (label: string): string =>
    labelParts(label)
      .map((p) => (p.key ? t(p.key as Key).replace('{n}', String(p.n)) : p.text))
      .join(', ')

  const toggle = (id: string): void =>
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  // Focus management: focus the close button on open, restore focus on close.
  // Also lock background page scroll while the modal is open.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()
    return () => {
      document.body.style.overflow = prevOverflow
      previouslyFocused?.focus()
    }
  }, [])

  // Escape closes; Tab is trapped inside the dialog.
  useEffect(() => {
    // The flatten dialog on top handles its own keys (Escape cancels it, not this one).
    if (flattening) return
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
        return
      }
      if (e.key !== 'Tab') return
      const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea, [tabindex]:not([tabindex="-1"])',
      )
      if (!focusables || focusables.length === 0) return
      const first = focusables[0]!
      const last = focusables[focusables.length - 1]!
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose, flattening])

  const allRows = buildHistoryRows(entries, versions ?? [], {
    // Nothing folds before the restore points are read: a missing one would only mean "not read".
    restorable: versions ? (restorable ?? (() => true)) : () => true,
    complete: !hasOlder,
  })
  // The newest row is the current version, whatever the filter hides above it.
  const currentKey = allRows[0]?.key
  const typed = query.trim()
  const needle = foldText(typed)
  /** Whether a version's tag (as stored or as shown) or comment contains the searched text. */
  const found = (v: VersionMeta | undefined): boolean => {
    if (!v) return false
    const known = v.tag ? suggestedTag(v.tag) : undefined
    const hay = [v.tag, known && t(`hist_tag_${known}` as Key), v.comment]
    return hay.some((h) => h && foldText(h).includes(needle))
  }
  const rows = allRows.filter((r) => (!taggedOnly || r.version?.tag) && (!needle || found(r.version)))
  // Restore points are being taken in this room (one older than the newest entry exists): the newest
  // entry's own is on its way, not missing — its tag and comment wait for it, saying so.
  const newest = allRows[0]
  const newestAt = newest?.type === 'entry' ? Date.parse(newest.entry.createdAt) : NaN
  const pointsTaken = Boolean(versions?.some((v) => v.historyId && Date.parse(v.createdAt) < newestAt))

  const canFlatten = Boolean(onFlatten && onFlattenPreview && versions && versions.length > 0)
  const flattenBlocked = busy || locked
  // Only a row the list shows can stay chosen: one the search or the filter hides is dropped.
  const keepRow = canFlatten ? rows.find((r) => r.key === keepKey && r.version) : undefined
  useEffect(() => {
    if (keepKey !== null && !keepRow) setKeepKey(null)
  }, [keepKey, keepRow])
  /** Who wrote a row, as the list shows it. */
  const authorOf = (row: HistoryRow): string =>
    row.type === 'entry' ? actorDisplayName(row.entry, row.entry.actor, agentPattern) : row.version.author
  // What flatten keeps from: the chosen row, else the newest restore point (and its row, if listed).
  const keepVersion = keepRow?.version ?? versions?.[0]
  const keepAuthorRow = keepRow ?? allRows.find((r) => r.version && r.version.version === keepVersion?.version)
  /** "version 54 — 13 Oct 10:51, sofia": how a version is named to the member. */
  const nameVersion = (v: VersionMeta, who: string): string =>
    t('hist_flatten_version')
      .replace('{n}', String(v.version))
      .replace('{when}', formatWhen(v.createdAt))
      .replace('{who}', who)
  /** Close the flatten dialog and give focus back to its trigger. */
  const endFlatten = (): void => {
    setFlattening(false)
    requestAnimationFrame(() => flattenRef.current?.focus())
  }

  const mergedNote = (n: number) =>
    n > 0 && <span className="hmerged">{n === 1 ? t('hist_merged_one') : t('hist_merged').replace('{n}', String(n))}</span>

  const changeOf = (e: HistoryEntry) => (
    <ChangeBody
      entry={e}
      open={expanded.has(e.id)}
      onToggle={() => toggle(e.id)}
      slideName={slideName}
      changeLabel={changeLabel}
    />
  )

  /**
   * The row's last cell: its actions, floated right, then its tag and comment flowing beside and
   * under them (spanning the Tag and Actions columns). Without versions, the actions alone.
   */
  const metaCell = (row: HistoryRow, actions: ReactNode) => {
    if (!withVersions) return hasActions ? <td className="hcol-act">{actions}</td> : null
    return (
      <td className="hcol-meta" colSpan={hasActions ? 2 : 1}>
        {actions && <div className="hmeta-actions">{actions}</div>}
        <VersionFields
          rowKey={row.key}
          version={row.version}
          when={formatWhen(row.type === 'entry' ? row.entry.createdAt : row.version.createdAt)}
          pending={row.key === currentKey && pointsTaken}
          onUpdate={onUpdateVersion}
          editing={editing?.key === row.key ? editing.field : null}
          onEdit={(field) => setEditing({ key: row.key, field })}
          onClose={(field) => {
            setEditing(null)
            focusField(row.key, field)
          }}
        />
      </td>
    )
  }

  /** Select a row as the version to flatten up to — or, selected already, back to none (the newest). */
  const toggleKeep = (key: string): void => setKeepKey((k) => (k === key ? null : key))

  /**
   * What makes a row selectable (admins, rows with a restore point): focusable, `aria-selected`, a
   * click anywhere but on its own controls (or on text being selected) toggles it, Space / Enter on
   * the focused row too, and the arrows move between the selectable rows.
   */
  const selectProps = (row: HistoryRow, className: string) => {
    if (!canFlatten || !row.version) return { className: className || undefined }
    const selected = keepKey === row.key
    return {
      className: `${className} is-selectable${selected ? ' is-kept' : ''}`.trim(),
      tabIndex: 0,
      'aria-selected': selected,
      onClick: (ev: ReactMouseEvent<HTMLTableRowElement>) => {
        const control = (ev.target as Element).closest(ROW_CONTROLS)
        if (control && ev.currentTarget.contains(control)) return
        if (window.getSelection()?.toString()) return
        toggleKeep(row.key)
      },
      onKeyDown: (ev: ReactKeyboardEvent<HTMLTableRowElement>) => {
        if (ev.target !== ev.currentTarget) return
        if (ev.key === ' ' || ev.key === 'Enter') {
          ev.preventDefault()
          toggleKeep(row.key)
        } else if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
          ev.preventDefault()
          const all = Array.from(ev.currentTarget.parentElement?.querySelectorAll<HTMLElement>('tr.is-selectable') ?? [])
          all[all.indexOf(ev.currentTarget) + (ev.key === 'ArrowDown' ? 1 : -1)]?.focus()
        }
      },
    }
  }

  const rowOf = (row: HistoryRow) => {
    const current = row.key === currentKey
    const confirming = confirm?.id === row.key ? confirm.action : null
    const ask = (action: 'restore' | 'copy') => setConfirm({ id: row.key, action })
    if (row.type === 'version') {
      const v = row.version
      return (
        <tr key={row.key} {...selectProps(row, 'hrow-older')}>
          <td className="hcol-when">
            <time dateTime={v.createdAt}>{formatWhen(v.createdAt)}</time>
          </td>
          <td className="hcol-who">
            {v.actorKind && (
              <span className={`hbadge ${v.actorKind}`}>
                {v.actorKind === 'ai' ? t('hist_ai') : t('hist_human')}
              </span>
            )}
            <span className="hactor">{v.author}</span>
          </td>
          <td className="hcol-what">
            <span className="hsec">{v.summary || v.label || t('hist_older_version')}</span>
            {loadDiff && (
              <VersionChange
                version={v.version}
                loadDiff={loadDiff}
                render={(e) => changeOf(e)}
              />
            )}
            {mergedNote(row.merged)}
          </td>
          {metaCell(
            row,
            hasActions && (
              <RowActions
                rowKey={row.key}
                onRestore={!current && onRestoreVersion ? () => onRestoreVersion(v) : undefined}
                onRestoreAsPart={
                  !current && onRestoreVersionAsPart ? () => onRestoreVersionAsPart(v) : undefined
                }
                available
                busy={busy}
                confirming={confirming}
                onAsk={ask}
                onCancel={() => setConfirm(null)}
              />
            ),
          )}
        </tr>
      )
    }
    const e = row.entry
    return (
      <tr key={row.key} {...selectProps(row, '')}>
        <td className="hcol-when">
          <time dateTime={e.createdAt}>{formatWhen(e.createdAt)}</time>
        </td>
        <td className="hcol-who">
          <span className={`hbadge ${e.kind}`}>
            {e.kind === 'ai' ? t('hist_ai') : t('hist_human')}
          </span>
          {/* `actor` is the fallback for a legacy entry written before the display
              name was recorded. */}
          <span className="hactor">{actorDisplayName(e, e.actor, agentPattern)}</span>
          {e.prompt && <span className="hprompt">“{e.prompt}”</span>}
        </td>
        <td className="hcol-what">
          {/* Where the edit landed. Display only and deliberately NOT a jump
              target — outline numbers drift as headings are added or removed. */}
          {e.section && (
            <span className="hsec">
              §{e.section}{e.sectionTitle ? ` · ${e.sectionTitle}` : ''}
            </span>
          )}
          {changeOf(e)}
          {mergedNote(row.merged)}
        </td>
        {metaCell(
          row,
          hasActions && (
            <RowActions
              rowKey={row.key}
              // The newest row is the current version: restoring it changes nothing
              // and a copy would only duplicate the part, so it offers neither.
              onRestore={!current && onRestore ? () => onRestore(e) : undefined}
              onRestoreAsPart={!current && onRestoreAsPart ? () => onRestoreAsPart(e) : undefined}
              available={restorable ? restorable(e) : true}
              busy={busy}
              confirming={confirming}
              onAsk={ask}
              onCancel={() => setConfirm(null)}
            />
          ),
        )}
      </tr>
    )
  }

  // Rendered in a portal on <body> so the fixed overlay is truly global and never
  // constrained (or scrolled) by a transformed/scrolling ancestor like .canvasWrap.
  return createPortal(
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        className="modal hist-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hist-title"
        ref={dialogRef}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <h2 id="hist-title">{t('history_title')}</h2>
          {/* The filters and "Flatten history…" sit right, beside the close button (AB#243). */}
          {withVersions && allRows.length > 0 && (
            <div className="htools">
              <label className="hfilter">
                <input type="checkbox" checked={taggedOnly} onChange={(e) => setTaggedOnly(e.target.checked)} />
                {t('hist_tagged_only')}
              </label>
              <div className="hsearch" role="search">
                <label htmlFor="hist-search" className="visually-hidden">{t('hist_search_label')}</label>
                <input
                  id="hist-search"
                  ref={searchRef}
                  type="search"
                  className="hsearch-input"
                  placeholder={t('hist_search_placeholder')}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    // Escape clears a search first — only an empty field lets it close the dialog.
                    if (e.key === 'Escape' && query) {
                      e.preventDefault()
                      e.stopPropagation()
                      setQuery('')
                    }
                  }}
                />
                {query && (
                  <button
                    type="button"
                    className="hpen"
                    aria-label={t('hist_search_clear')}
                    onClick={() => {
                      setQuery('')
                      searchRef.current?.focus()
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
              {canFlatten && (
                <span className="hflatten">
                  <button
                    ref={flattenRef}
                    className="hrestore"
                    disabled={flattenBlocked}
                    {...(flattenBlocked ? { 'aria-describedby': 'hist-flatten-reason' } : {})}
                    onClick={() => setFlattening(true)}
                  >
                    {t('hist_flatten')}
                  </button>
                  {flattenBlocked && <span id="hist-flatten-reason" className="hreason">{t('hist_flatten_busy')}</span>}
                </span>
              )}
            </div>
          )}
          <button ref={closeRef} className="modal-close" aria-label={t('close')} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          {allRows.length === 0 ? (
            <p className="history-empty">{t('hist_empty')}</p>
          ) : rows.length === 0 ? (
            <p className="history-empty" role="status">
              {needle ? t('hist_no_match').replace('{q}', typed) : t('hist_no_tagged')}
            </p>
          ) : (
            <table
              className="htable"
              {...(canFlatten
                ? { role: 'grid', 'aria-labelledby': 'hist-title', 'aria-describedby': selectHintId }
                : {})}
            >
              <thead>
                <tr>
                  <th scope="col">{t('hist_when')}</th>
                  <th scope="col">{t('hist_who')}</th>
                  <th scope="col">{t('hist_what')}</th>
                  {withVersions && <th scope="col" className="hcol-tag">{t('hist_tag_col')}</th>}
                  {hasActions && <th scope="col" className="hcol-act">{t('hist_actions')}</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map(rowOf)}
              </tbody>
            </table>
          )}
          {canFlatten && (
            <p id={selectHintId} className="visually-hidden">{t('hist_flatten_select_hint')}</p>
          )}
          {withVersions && hasOlder && onShowOlder && (
            <div className="holder">
              <button className="hrestore" onClick={onShowOlder} disabled={loadingOlder} aria-busy={loadingOlder}>
                {loadingOlder ? t('hist_loading') : t('hist_show_older')}
              </button>
            </div>
          )}
        </div>
        {flattening && keepVersion && onFlattenPreview && onFlatten && (
          <FlattenHistoryDialog
            keep={keepVersion.version}
            keepName={nameVersion(keepVersion, keepAuthorRow ? authorOf(keepAuthorRow) : keepVersion.author)}
            newest={keepVersion.version === versions?.[0]?.version}
            onPreview={onFlattenPreview}
            onConfirm={onFlatten}
            onClose={endFlatten}
            onDone={() => {
              // The chosen version is now the oldest: nothing to keep it chosen for.
              setKeepKey(null)
              endFlatten()
            }}
          />
        )}
      </div>
    </div>,
    document.body,
  )
}

/** A row's own controls: a click on one acts on it and never (un)selects the row. */
const ROW_CONTROLS = 'button, a, input, textarea, select, label, [role="combobox"], [role="option"]'

/** A version's two member-written fields. */
type VersionField = 'tag' | 'comment'

/** The id of a field's pen ("Edit tag") and of its "Add …" action — where focus returns after editing. */
const penId = (rowKey: string, field: VersionField): string => `hist-${field}-edit-${rowKey}`
const addId = (rowKey: string, field: VersionField): string => `hist-${field}-add-${rowKey}`

/** Focus the field's pen once the row has re-rendered — or its "Add …" when the field is now empty. */
function focusField(rowKey: string, field: VersionField): void {
  requestAnimationFrame(() =>
    (document.getElementById(penId(rowKey, field)) ?? document.getElementById(addId(rowKey, field)))?.focus(),
  )
}

/** Text as the search compares it: lower case, accents dropped ("Été" → "ete"). */
function foldText(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()
}

/**
 * What one row changed: a slides entry's lines, a grid entry's cells, or a prose window as an
 * inline diff — each clipped to CHANGE_LINES lines behind "Show more" when longer.
 */
function ChangeBody({
  entry: e,
  open,
  onToggle,
  slideName,
  changeLabel,
}: {
  entry: HistoryEntry
  open: boolean
  onToggle: () => void
  slideName: ((slide: string) => string) | undefined
  changeLabel: (label: string) => string
}) {
  if (e.slides?.length) {
    /* A SLIDES entry: no text window, one readable line per slide it touched. */
    return (
      <ClampedChange open={open} onToggle={onToggle} estimate={e.slides.length}>
        <ul className="hslides">
          {e.slides.map((c, i) => (
            <li key={`${c.slide}-${i}`}>
              {slideName && <span className="hslide">{slideName(c.slide)}</span>}
              {slideName && ' '}
              {changeLabel(c.label)}
            </li>
          ))}
        </ul>
      </ClampedChange>
    )
  }
  if (isGridEntry(e)) return <GridChange entry={e} open={open} onToggle={onToggle} />
  const text = (e.contextBefore ?? '') + (e.before ?? '') + (e.after ?? '') + (e.contextAfter ?? '')
  return (
    <ClampedChange open={open} onToggle={onToggle} estimate={estimateLines(text)}>
      <span className="hdiff">
        {e.contextBefore && <span className="diff-ctx">{e.contextBefore}</span>}
        {e.before && <del className="diff-del">{e.before}</del>}
        {e.after && <ins className="diff-ins">{e.after}</ins>}
        {e.contextAfter && <span className="diff-ctx">{e.contextAfter}</span>}
      </span>
    </ClampedChange>
  )
}

/**
 * A change detail capped at CHANGE_LINES lines. Whether it is longer is measured once laid out (and
 * again whenever its size changes); where nothing is laid out, `estimate` (its line count) decides. A
 * short detail shows no toggle; a long one, closed, is clipped with a fade and offers "Show more".
 */
function ClampedChange({
  open,
  onToggle,
  estimate,
  children,
}: {
  open: boolean
  onToggle: () => void
  estimate: number
  children: ReactNode
}) {
  const { t } = useLang()
  const id = useId()
  const ref = useRef<HTMLDivElement>(null)
  const [measured, setMeasured] = useState<boolean | null>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = (): void => {
      const line = parseFloat(getComputedStyle(el).lineHeight)
      // Not laid out (or no line height in px): the estimate decides.
      if (el.scrollHeight === 0 || !Number.isFinite(line) || line <= 0) setMeasured(null)
      else setMeasured(el.scrollHeight > line * CHANGE_LINES + 1)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const long = measured ?? estimate > CHANGE_LINES
  return (
    <>
      <div ref={ref} id={id} className={`hclamp${long && !open ? ' is-clamped' : ''}`}>
        {children}
      </div>
      {long && (
        <button className="hmore" aria-expanded={open} aria-controls={id} onClick={onToggle}>
          {open ? t('show_less') : t('show_more')}
        </button>
      )}
    </>
  )
}

/**
 * An older version's change, read from the server (AB#240) and rendered in the log's own
 * vocabulary — the diff is shaped into an entry so prose, grid and slides read exactly like the rows
 * above them.
 */
function VersionChange({
  version,
  loadDiff,
  render,
}: {
  version: number
  loadDiff: (version: number) => Promise<VersionDiff>
  render: (entry: HistoryEntry) => ReactNode
}) {
  const { t } = useLang()
  const [diff, setDiff] = useState<VersionDiff | null | 'failed'>(null)
  useEffect(() => {
    let live = true
    loadDiff(version)
      .then((d) => live && setDiff(d))
      .catch(() => live && setDiff('failed'))
    return () => {
      live = false
    }
  }, [version, loadDiff])
  if (diff === null) return <span className="hnote">{t('hist_change_loading')}</span>
  if (diff === 'failed') return <span className="hnote">{t('hist_change_failed')}</span>
  return <>{render(diffAsEntry(diff))}</>
}

/** A version diff in the shape of a history entry, for the shared change renderer. */
function diffAsEntry(d: VersionDiff): HistoryEntry {
  const base: HistoryEntry = { id: `v${d.version}`, actor: '', kind: 'human', createdAt: '' }
  const c = d.change
  if (c.format === 'md') return { ...base, start: c.start, before: c.before, after: c.after }
  if (c.format === 'grid') {
    return { ...base, cells: c.cells, structure: c.structure, ...(c.truncated ? { truncated: true } : {}) }
  }
  return { ...base, slides: c.slides }
}

/**
 * A row's tag and comment. Tag and comment behave alike:
 * - empty: a plain "Add a tag" / "Add a comment" action, side by side on the tag line;
 * - set: the tag as a chip with an × (removing = untagging, no confirm: it is reversible) and the
 *   comment as text, each with a pen at the right of the tag column that switches that one field
 *   into edit mode.
 * Without a restore point there is nothing to tag: both actions render disabled with the reason in
 * words (a disabled control takes no focus, so a tooltip would never reach everyone) — "Saving this
 * version…" while the newest one's is on its way (`pending`). Without `onUpdate` (a viewer) both are
 * read only.
 */
function VersionFields({
  rowKey,
  version,
  when,
  pending,
  onUpdate,
  editing,
  onEdit,
  onClose,
}: {
  rowKey: string
  version: VersionMeta | undefined
  when: string
  pending: boolean
  onUpdate: ((version: number, patch: VersionPatch) => Promise<unknown>) | undefined
  editing: VersionField | null
  onEdit: (field: VersionField) => void
  onClose: (field: VersionField) => void
}) {
  const { t } = useLang()
  const [removing, setRemoving] = useState(false)

  if (!version) {
    if (!onUpdate) return null
    const reasonId = `hist-tag-reason-${rowKey}`
    return (
      <div className="htagline">
        <button className="hadd" disabled aria-describedby={reasonId}>{t('hist_tag_add')}</button>
        <button className="hadd" disabled aria-describedby={reasonId}>{t('hist_comment_add')}</button>
        <span id={reasonId} className="hreason">
          {pending ? t('hist_saving_version') : t('hist_no_restore_point_tag')}
        </span>
      </div>
    )
  }

  const save = (patch: VersionPatch, field: VersionField): Promise<void> =>
    onUpdate ? onUpdate(version.version, patch).then(() => onClose(field)) : Promise.resolve()

  const removeTag = (): void => {
    setRemoving(true)
    save({ tag: null }, 'tag')
      .catch(() => undefined) // the parent says why; the chip stays
      .finally(() => setRemoving(false))
  }

  const tagName = version.tag ? tagDisplayName(version.tag, t) : ''
  return (
    <>
      <div className="htagline">
        {editing === 'tag' && onUpdate ? (
          <TagEditor
            label={t('hist_tag_label').replace('{when}', when)}
            value={version.tag}
            onSave={(tag) => {
              if (tag !== (version.tag ?? null)) return save({ tag }, 'tag')
              onClose('tag') // unchanged: nothing to send
              return Promise.resolve()
            }}
            onCancel={() => onClose('tag')}
          />
        ) : (
          <>
            {version.tag ? (
              <TagBadge tag={version.tag}>
                {onUpdate && (
                  <button
                    className="htag-x"
                    aria-label={t('hist_tag_remove').replace('{tag}', tagName.toLowerCase())}
                    disabled={removing}
                    onClick={removeTag}
                  >
                    ×
                  </button>
                )}
              </TagBadge>
            ) : (
              onUpdate && (
                <button id={addId(rowKey, 'tag')} className="hadd" onClick={() => onEdit('tag')}>
                  {t('hist_tag_add')}
                </button>
              )
            )}
            {onUpdate && !version.comment && editing !== 'comment' && (
              <button id={addId(rowKey, 'comment')} className="hadd" onClick={() => onEdit('comment')}>
                {t('hist_comment_add')}
              </button>
            )}
            {onUpdate && version.tag && (
              <button
                id={penId(rowKey, 'tag')}
                className="hpen hpen-tag"
                aria-label={t('hist_tag_edit')}
                title={t('hist_tag_edit')}
                onClick={() => onEdit('tag')}
              >
                ✎
              </button>
            )}
          </>
        )}
      </div>
      {editing === 'comment' && onUpdate ? (
        <CommentEditor
          label={t('hist_comment_label').replace('{when}', when)}
          value={version.comment ?? ''}
          onSave={(comment) => save({ comment }, 'comment')}
          onCancel={() => onClose('comment')}
        />
      ) : (
        version.comment && (
          <p className="hcomment">
            {/* Floated, before the text: it sits at the right of the tag column, the text wraps. */}
            {onUpdate && (
              <button
                id={penId(rowKey, 'comment')}
                className="hpen hpen-comment"
                aria-label={t('hist_comment_edit')}
                title={t('hist_comment_edit')}
                onClick={() => onEdit('comment')}
              >
                ✎
              </button>
            )}
            {version.comment}
          </p>
        )
      )}
    </>
  )
}

/** The suggested tag `tag` stands for (case aside), or undefined for a free one. */
function suggestedTag(tag: string): SuggestedVersionTag | undefined {
  const low = tag.toLowerCase()
  return SUGGESTED_VERSION_TAGS.find((s) => s === low)
}

/** A tag as members read it: a suggested one by its translated name, any other as typed. */
function tagDisplayName(tag: string, t: (key: Key) => string): string {
  const known = suggestedTag(tag)
  return known ? t(`hist_tag_${known}` as Key) : tag
}

/** A tag as a chip: a suggested one keeps its colour and its translated name, any other is neutral. */
function TagBadge({ tag, children }: { tag: VersionTag; children?: ReactNode }) {
  const { t } = useLang()
  const known = suggestedTag(tag)
  return (
    <span className={`htagbadge ${known ?? 'free'}`}>
      {tagDisplayName(tag, t)}
      {children}
    </span>
  )
}

/**
 * The tag in edit mode: a combobox taking any text, with the suggested tags offered as a list
 * (filtered by what is typed), then Save / Cancel. Enter saves (the highlighted suggestion when the
 * list is open), a suggestion picked with the mouse is saved at once, Escape cancels. Saving an
 * empty field removes the tag; a suggestion typed by its shown name ("Gold", or "Or" in French) is
 * saved as that suggestion. A refusal keeps the field open with what was typed (the parent says why).
 */
function TagEditor({
  label,
  value,
  onSave,
  onCancel,
}: {
  label: string
  value: VersionTag | undefined
  onSave: (tag: VersionTag | null) => Promise<unknown>
  onCancel: () => void
}) {
  const { t } = useLang()
  const listId = useId()
  const hintId = useId()
  const nameOf = (tag: string): string => tagDisplayName(tag, t)
  const shown = value ? nameOf(value) : ''
  const [text, setText] = useState(shown)
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const [saving, setSaving] = useState(false)

  const typed = text.trim().toLowerCase()
  const matches = SUGGESTED_VERSION_TAGS.filter(
    (s) => !typed || text === shown || s.includes(typed) || nameOf(s).toLowerCase().includes(typed),
  )

  /** What the field's text means as a tag: '' = none, a suggestion's key or name = that key. */
  const toTag = (raw: string): VersionTag | null => {
    const trimmed = raw.trim()
    if (!trimmed) return null
    const low = trimmed.toLowerCase()
    return SUGGESTED_VERSION_TAGS.find((s) => s === low || nameOf(s).toLowerCase() === low) ?? trimmed
  }

  const save = (tag: VersionTag | null): void => {
    if (saving) return
    setOpen(false)
    setActive(-1)
    setSaving(true)
    // Success closes the editor (the parent unmounts it); only a refusal comes back here.
    onSave(tag).catch(() => setSaving(false))
  }

  const pick = (tag: SuggestedVersionTag): void => {
    setText(nameOf(tag))
    save(tag)
  }

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (matches.length === 0) return
      e.preventDefault()
      const step = e.key === 'ArrowDown' ? 1 : -1
      setOpen(true)
      setActive((i) => (i < 0 && step < 0 ? matches.length - 1 : (i + step + matches.length) % matches.length))
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      const option = open && active >= 0 ? matches[active] : undefined
      if (option) pick(option)
      else save(toTag(text))
      return
    }
    if (e.key === 'Escape') {
      // Cancels the edit — and only that: the dialog stays open.
      e.preventDefault()
      e.stopPropagation()
      onCancel()
    }
  }

  const expanded = open && matches.length > 0
  return (
    <span className="hfield-edit">
      <span className="htag-combo">
        <input
          type="text"
          className="htag-input"
          role="combobox"
          aria-expanded={expanded}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-label={label}
          aria-describedby={hintId}
          aria-activedescendant={expanded && active >= 0 ? `${listId}-${active}` : undefined}
          placeholder={t('hist_tag_placeholder')}
          maxLength={VERSION_TAG_MAX}
          value={text}
          autoFocus
          onChange={(e) => {
            setText(e.target.value)
            setOpen(true)
            setActive(-1)
          }}
          onClick={() => setOpen(true)}
          onKeyDown={onKeyDown}
          onBlur={() => setOpen(false)}
        />
        <span id={hintId} className="visually-hidden">{t('hist_tag_hint')}</span>
        {/* Kept in the DOM, hidden when closed, so aria-controls always resolves. */}
        <ul
          id={listId}
          role="listbox"
          aria-label={t('hist_tag_suggestions')}
          className={`htag-list${expanded ? '' : ' htag-list-hidden'}`}
        >
          {expanded &&
            matches.map((s, i) => (
              <li
                key={s}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                className={`htag-option${i === active ? ' is-active' : ''}`}
                // onMouseDown, not onClick: the input's blur would close the list first.
                onMouseDown={(e) => {
                  e.preventDefault()
                  pick(s)
                }}
                onMouseEnter={() => setActive(i)}
              >
                <span className={`htagbadge ${s}`}>{nameOf(s)}</span>
              </li>
            ))}
        </ul>
      </span>
      <span className="hrestore-confirm">
        <button className="mini ok" disabled={saving} onClick={() => save(toTag(text))}>
          {t('hist_comment_save')}
        </button>
        <button className="mini ghost" onClick={onCancel}>{t('cancel')}</button>
      </span>
    </span>
  )
}

/**
 * The comment in edit mode, under the tag line and across the whole cell (below the row's actions).
 * Save sends the trimmed text (an emptied comment clears it), Escape cancels; Enter is a new line.
 * A refusal keeps the draft (the parent says why). Focus starts in the box.
 */
function CommentEditor({
  label,
  value,
  onSave,
  onCancel,
}: {
  label: string
  value: string
  onSave: (comment: string | null) => Promise<unknown>
  onCancel: () => void
}) {
  const { t } = useLang()
  const [draft, setDraft] = useState(value)
  const [saving, setSaving] = useState(false)
  const save = (): void => {
    setSaving(true)
    onSave(draft.trim() || null).catch(() => setSaving(false))
  }
  return (
    <div className="hcomment-edit">
      <textarea
        aria-label={label}
        value={draft}
        maxLength={VERSION_COMMENT_MAX}
        rows={3}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault()
            e.stopPropagation()
            onCancel()
          }
        }}
      />
      <span className="hrestore-confirm">
        <button className="mini ok" disabled={saving} onClick={save}>
          {t('hist_comment_save')}
        </button>
        <button className="mini ghost" onClick={onCancel}>{t('cancel')}</button>
      </span>
    </div>
  )
}

/**
 * One row's actions. `available` false = the entry has no restore point: both buttons render
 * disabled and the reason is written next to them (a disabled button cannot be focused, so a tooltip
 * alone would never reach a keyboard or screen-reader user). `busy` (an agent is editing) blocks the
 * in-place Restore only — a copy just reads.
 */
function RowActions({
  rowKey,
  onRestore,
  onRestoreAsPart,
  available,
  busy,
  confirming,
  onAsk,
  onCancel,
}: {
  rowKey: string
  onRestore: (() => void) | undefined
  onRestoreAsPart: (() => void) | undefined
  available: boolean
  busy: boolean
  confirming: 'restore' | 'copy' | null
  onAsk: (action: 'restore' | 'copy') => void
  onCancel: () => void
}) {
  const { t } = useLang()
  if (!onRestore && !onRestoreAsPart) return null
  if (confirming === 'restore' && onRestore && available && !busy) {
    return (
      <span className="hrestore-confirm">
        <button className="mini ok" onClick={() => { onCancel(); onRestore() }}>{t('hist_restore_q')}</button>
        <button className="mini ghost" onClick={onCancel}>{t('cancel')}</button>
      </span>
    )
  }
  if (confirming === 'copy' && onRestoreAsPart && available) {
    return (
      <span className="hrestore-confirm">
        <button className="mini ok" onClick={() => { onCancel(); onRestoreAsPart() }}>{t('hist_restore_part_q')}</button>
        <button className="mini ghost" onClick={onCancel}>{t('cancel')}</button>
      </span>
    )
  }
  const reasonId = `hist-reason-${rowKey}`
  return (
    <span className="hactions">
      {onRestore && (
        <button
          className="hrestore"
          disabled={!available || busy}
          title={!available ? t('hist_no_restore_point') : busy ? t('busy_generic') : t('hist_restore_title')}
          {...(!available ? { 'aria-describedby': reasonId } : {})}
          onClick={() => onAsk('restore')}
        >
          ↶ {t('hist_restore')}
        </button>
      )}
      {onRestoreAsPart && (
        <button
          className="hrestore hcopy"
          disabled={!available}
          title={available ? t('hist_restore_part_title') : t('hist_no_restore_point')}
          {...(!available ? { 'aria-describedby': reasonId } : {})}
          onClick={() => onAsk('copy')}
        >
          ⧉ {t('hist_restore_part')}
        </button>
      )}
      {!available && <span id={reasonId} className="hreason">{t('hist_no_restore_point')}</span>}
    </span>
  )
}

/**
 * A GRID entry's change. Not a markdown window — a grid holds no text offsets — so the same
 * red/green diff vocabulary is applied per CELL, each named by the labels FROZEN at write time
 * (`rowLabel` / `colLabel`), which is what lets a log written weeks ago still read correctly
 * after the rows have been reordered and the columns renamed.
 *
 * One entry can hold up to GRID_HISTORY_CELLS cells — an agent filling a pricing grid writes one
 * entry, not two hundred — so the list is clipped behind the same "Show more" the prose rows use.
 */
function GridChange({ entry, open, onToggle }: { entry: HistoryEntry; open: boolean; onToggle: () => void }) {
  const { t } = useLang()
  const cells = entry.cells ?? []
  const structure = entry.structure ?? []
  const estimate =
    (structure.length > 0 ? 1 : 0) +
    cells.reduce((n, c) => n + estimateLines(`${c.colLabel ?? c.colId} · ${c.rowLabel ?? ''} ${c.before ?? ''}${c.after ?? ''}`), 0)

  return (
    <>
      <ClampedChange open={open} onToggle={onToggle} estimate={estimate}>
        {structure.length > 0 && (
          <span className="hstruct">
            {structure
              .map((s) => `${s.count} ${t(`hist_op_${s.op}` as 'hist_op_insertRow')}${s.label ? ` (${s.label})` : ''}`)
              .join(' · ')}
          </span>
        )}
        <ul className="hcells">
          {cells.map((c) => (
            <li key={`${c.rowId}/${c.colId}`}>
              <span className="hcell-at">
                {c.colLabel ?? c.colId}
                {c.rowLabel ? ` · ${c.rowLabel}` : ''}
              </span>
              <span className="hdiff">
                {c.before && <del className="diff-del">{c.before}</del>}
                {c.after && <ins className="diff-ins">{c.after}</ins>}
              </span>
            </li>
          ))}
        </ul>
      </ClampedChange>
      {/* The write was larger than the log records — say so rather than let the list read as
          complete. GRID_HISTORY_CELLS is the cap; see backend/domain/grid.ts#cellChanges. */}
      {entry.truncated && <span className="htrunc">{t('hist_grid_more')}</span>}
    </>
  )
}

function formatWhen(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
